import { useHandler } from "@redotech/react-util/hook";
import { AssistantMessageContent } from "@redotech/redo-model/agents/llm-messages";
import { MessageAuthorInfo } from "@redotech/redo-web/ai/chat/contexts/message-author-context";
import { MutableRefObject, useState } from "react";
import { USE_IN_CHAT_ESCALATION } from "./feature-flags";
import {
  useEscalationSubscription,
  type HumanMessage,
} from "./use-escalation-subscription";

interface UseHumanMessageHandlerParams {
  addMessage: (message: {
    id: string;
    role: "assistant";
    content: string | AssistantMessageContent[];
  }) => void;
  processedMessageIdsRef: MutableRefObject<Set<string>>;
  authorRegistryRef: MutableRefObject<Map<string, MessageAuthorInfo>>;
}

function buildAttachmentContent(
  message: HumanMessage,
): AssistantMessageContent[] {
  const contentParts: AssistantMessageContent[] = [];

  if (message.body) {
    const cleanedBody = message.body
      .replace(/\[Attachment: "[^"]*"\]/g, "")
      .trim();
    if (cleanedBody) {
      contentParts.push({ type: "text" as const, text: cleanedBody });
    }
  }

  for (const attachment of message.attachments!) {
    const isImage = attachment.contentType?.startsWith("image/");
    const isVideo = attachment.contentType?.startsWith("video/");

    const attachmentData = JSON.stringify({
      url: attachment.url,
      type: attachment.contentType,
      name:
        isImage || isVideo ? attachment.name : attachment.name || "Attachment",
    });

    const marker = isImage
      ? "image-data"
      : isVideo
        ? "video-data"
        : "document-data";

    contentParts.push({
      type: "text" as const,
      text: `[[[[${marker}:${attachmentData}]]]]`,
    });
  }

  return contentParts;
}

export function useHumanMessageHandler({
  addMessage,
  processedMessageIdsRef,
  authorRegistryRef,
}: UseHumanMessageHandlerParams) {
  const [agentName, setAgentName] = useState<string | null>(null);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | null>(null);

  const handleHumanMessage = useHandler((message: HumanMessage) => {
    if (!USE_IN_CHAT_ESCALATION) return;

    if (processedMessageIdsRef.current.has(message.id)) {
      return;
    }
    processedMessageIdsRef.current.add(message.id);

    if (message.authorName && message.authorName !== agentName) {
      setAgentName(message.authorName);
    }
    if (message.authorAvatarUrl && message.authorAvatarUrl !== agentAvatarUrl) {
      setAgentAvatarUrl(message.authorAvatarUrl);
    }

    const content: string | AssistantMessageContent[] =
      message.attachments && message.attachments.length > 0
        ? buildAttachmentContent(message)
        : message.body;

    const messageId = `human-${message.id}`;

    authorRegistryRef.current.set(messageId, {
      authorName: message.authorName ?? null,
      authorAvatarUrl: message.authorAvatarUrl ?? null,
    });

    addMessage({ id: messageId, role: "assistant", content });
  });

  useEscalationSubscription(
    USE_IN_CHAT_ESCALATION ? handleHumanMessage : undefined,
  );

  return { agentName, setAgentName, agentAvatarUrl, setAgentAvatarUrl };
}
